import { createCombinedEmailRunner, runCombinedEmailForCompanies } from './combinedEmailLoader';
import { foundEmailLabel, foundEmailsFromTerminal, verifiableEmails, withLiveFoundEmails } from './foundEmail';
import { useCompanySelection } from "./useCompanySelection";
import PushToCrmMenu from "./PushToCrmMenu";
import { isCompanyActionEligible } from "../../helper/companyEligibility";
import React, { useMemo, useState, useRef, useEffect, useCallback } from 'react';
import { SelectColumn } from 'react-data-grid';
import 'react-data-grid/lib/styles.css';

import {
  Badge,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Divider,
  Avatar,
  IconButton,
  ListItemText,
  Menu,
  MenuItem,
  Paper,
  Tooltip,
  Typography,
  useTheme,
  Snackbar,
  Alert,
} from '@mui/material';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import BuildingIcon from '@mui/icons-material/Business';
import ViewColumnIcon from '@mui/icons-material/ViewColumn';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CloseIcon from '@mui/icons-material/Close';
import useIsMobile from '../../hooks/useIsMobile';
import { MOBILE_LIST_ROW_MIN_HEIGHT_PX } from '../../theme/mobileLayout';
import defaultImageUrl from '../../assets/defaultImage.svg';
import VerifiedIcon from '@mui/icons-material/Verified';
import {
  ALL_COLUMNS,
  DEFAULT_VISIBLE,
  copyToClipboard,
  insertLeadContactColumns,
  mergeLeadDerivedStatusMap,
  normalizeLeadDerivedColumns,
  renderHeadcountBucket,
  renderSpreadsheetHeaderCell,
  sortGridRows
} from './EnrichmentGrid.helpers';
import {
  applyEmailVerificationUpdate,
  buildGridColumns,
  getActiveColumnKeys,
  getBodyRowHeight as calculateBodyRowHeight,
  getContentGridHeight,
  getLatestEnrichmentSummary,
  getLatestWorkspaceAction,
  isLongFormEnrichmentAction,
  mergeWorkspaceColumns,
  mergeWorkspaceStatusMap,
  reconcileSortColumns,
  setStatusEntry,
} from './EnrichmentGrid.state';
import { classifySingleAiEnrichment } from './classifySingleAiEnrichment';
import CompanyModal from '../../components/CompanyModal/CompanyModal';
import { registerCompanyInquiry } from '../../api/companyApi';
import {
  executeAiEnrichmentApi,
  findEmailBulkApi,
  findSpreadsheetEmailBulkApi,
  verifyEmailBulkApi,
  verifySpreadsheetEmailBulkApi,
} from "../../api/enrichmentApi";
import {
  DEFAULT_SPREADSHEET_MODEL_ID,
  getSpreadsheetModelById
} from "../../helper/aiEnrichment";
import {
  extractDomain,
  fmtPhone,
  formatVerifyEmailCellError,
  groupCompaniesForSpreadsheet,
  normalizeEmails,
  parseApiError
} from "../../helper/spreadsheetView";
import {
  createGeneratedColumnLoadingGate,
  deriveGeneratedColumnStatus,
  GENERATED_COLUMN_CELL_CLASS,
  GENERATED_COLUMN_HEADER_PROCESSING_CLASS,
  GENERATED_COLUMN_PROCESSING_CLASS,
  GENERATED_COLUMN_RESULT_CLASS,
  GENERATED_ROW_HIGHLIGHT_CLASS,
  isGeneratedCellResultSettled,
  isGeneratedColumnHighlightActive,
  rowHasGeneratedContent,
} from "../../helper/generatedColumn";
import { HOME_NEW_RESULT_FLASH_CLASS } from "../../helper/savedSearchNewResults";
import EnrichmentGridNewCompanyChip, { toNewCompanyIdSet } from "./EnrichmentGridNewCompanyChip";
import {
  GeneratedColumnHeaderCell,
  GeneratedColumnResultCell,
} from "./GeneratedColumnLoading";
import SpreadsheetActionsMenu from "./SpreadsheetActionsMenu";
import SearchAiEnrichmentReview from './SearchAiEnrichmentReview';
import submitAiEnrichmentBatch from './submitAiEnrichmentBatch';
import useSearchAiEnrichmentReview from './useSearchAiEnrichmentReview';
import {batchSubmittedNotice, batchSubmissionErrorNotice} from './aiEnrichmentNotices';
import {settleRunningEntries, watchAiBatch} from './gridAiBatchRun';
import {classifyLeadRow, isLeadRun} from './leadOutcome';
import useGridActions from "./useGridActions";
import useAutoRevealColumns from "./useAutoRevealColumns";
import { NOT_REPORTED } from "../../helper/metricBuckets";
import {
  GridBody,
  GridSection,
  GridTopBar,
  MainContainer,
  StyledDataGrid,
} from "./EnrichmentGrid.styles";

/** Shared +N chip for multi-email / multi-location cells. */
const MULTI_VALUE_COUNT_CHIP_SX = {
  height: '18px',
  fontSize: '11px',
  borderRadius: '6px',
  flex: '0 0 auto',
};

const ROW_INTERACTIVE_SELECTOR =
  'button, a, input, textarea, select, label, [role="checkbox"], [role="button"], [role="menuitem"]';

const isSelectColumnKey = (key) =>
  key === SelectColumn.key
  || key === 'select'
  || key === 'select-row'
  || key === 'rdg-select-column';

const DATA_ENRICHMENT_COST_LABEL = '1 credit';
const STORAGE_KEY_VISIBLE_COLS = 'alphasearch:detailsGrid:visibleColumns:v1';

const EnrichmentGrid = ({
  companies, retainedCompanies,
  selectedRows: requestedSelectedRows = new Set(),
  onSelectedRowsChange,
  draftSessionId = null, workspaceId = null,
  onDraftSessionUsed = () => {},
  searchEnrichmentContext = null,
  crmSourceDirty = false,
  workspaceEnrichmentColumns = [], workspaceEnrichmentColumnLabels = {},
  workspaceEnrichmentStatusMap = {}, workspaceEnrichmentSummaryStatusMap = null,
  /** When set, scroll the grid to this generated column key (workspace prompt / action key). */
  focusGeneratedColumnKey = null,
  onFocusGeneratedColumnConsumed,
  /** Home "+N new" (Spec §7): rows to mark New; `flashNewResultRows` adds a temporary row flash. */
  newCompanyIds = null,
  flashNewResultRows = false,
}) => {
  const theme = useTheme();
  const isMobile = useIsMobile();

  const [visibleKeys, setVisibleKeys] = useState(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY_VISIBLE_COLS);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed) && parsed.length > 0) return new Set(parsed);
      }
    } catch {
      // ignore
    }
    return new Set(DEFAULT_VISIBLE);
  });

  const [columnsMenuAnchor, setColumnsMenuAnchor] = useState(null);
  const [emailsDialog, setEmailsDialog] = useState({ open: false, rowId: null, companyName: '', emails: [] });
  const [locationsDialog, setLocationsDialog] = useState({
    open: false,
    rowId: null,
    companyName: '',
    locations: [],
  });
  const [companyDetailOpen, setCompanyDetailOpen] = useState(false);
  const [companyDetail, setCompanyDetail] = useState(null);

  const gridWrapRef = useRef(null);
  const dataGridRef = useRef(null);
  const columnsButtonRef = useRef(null);
  const actionsButtonRef = useRef(null), combinedEmailRunner = useRef(null);
  if (!combinedEmailRunner.current) combinedEmailRunner.current = createCombinedEmailRunner();
  const [containerWidth, setContainerWidth] = useState(0);
  
  const [aiPrompt, setAiPrompt] = useState('');
  const [aiModelId, setAiModelId] = useState(DEFAULT_SPREADSHEET_MODEL_ID);

  const [statusMap, setStatusMap] = useState({});
  const [enrichmentColumns, setEnrichmentColumns] = useState([]);
  const [latestEnrichmentAction, setLatestEnrichmentAction] = useState(null);
  const [aiRunLabels, setAiRunLabels] = useState({}), batchWatchers = useRef(new Set());
  useEffect(() => () => batchWatchers.current.forEach((stop) => stop()), []);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });
  const {
    actionsAnchor, actionsView, dataEnrichmentSelection, setActionsView,
    setDataEnrichmentSelection, openActions, closeActions, pending: actionsPending, runPaidAction,
    isEmailPending, runEmailVerification,
  } = useGridActions(setSnackbar);
  const [emailVerification, setEmailVerification] = useState({});
  // FE #750: found emails from this session's Find Verified Emails runs, by row id.
  const [liveFoundEmails, setLiveFoundEmails] = useState({});
  const [sortColumns, setSortColumns] = useState([]);

  const HEADER_HEIGHT = 44;
  const ENRICHED_ROW_HEIGHT = 140;
  // V2 rows match the 44px header band. Mobile keeps larger touch targets.
  // Row height is fixed (not stretched to fill a viewport) so table height
  // follows pagination page size.
  const BASE_ROW_HEIGHT = isMobile
    ? MOBILE_LIST_ROW_MIN_HEIGHT_PX
    : 44;

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY_VISIBLE_COLS, JSON.stringify(Array.from(visibleKeys)));
    } catch {
      // ignore
    }
  }, [visibleKeys]);

  // Sync Enrich Workspace answers from the parent (workspace.columns / rows).
  useEffect(() => {
    const columns = Array.isArray(workspaceEnrichmentColumns)
      ? workspaceEnrichmentColumns.filter(Boolean)
      : [];
    if (!columns.length) return;

    setEnrichmentColumns((current) => mergeWorkspaceColumns(current, columns));
    setStatusMap((current) =>
      mergeWorkspaceStatusMap(current, columns, workspaceEnrichmentStatusMap)
    );
    const summaryStatusMap = workspaceEnrichmentSummaryStatusMap || workspaceEnrichmentStatusMap;
    setLatestEnrichmentAction((current) =>
      getLatestWorkspaceAction(current, columns, summaryStatusMap));
  }, [workspaceEnrichmentColumns, workspaceEnrichmentStatusMap, workspaceEnrichmentSummaryStatusMap]);

  const groupedCompanies = useMemo(
    () => groupCompaniesForSpreadsheet(withLiveFoundEmails(companies || [], liveFoundEmails)),
    [companies, liveFoundEmails]
  );

  const { companyById, rowById, rows, selectedRows, unavailableIds, changeSelection, selectionColumn } = useCompanySelection(
    groupedCompanies, requestedSelectedRows, onSelectedRowsChange, retainedCompanies);
  const markColumnDecided = useAutoRevealColumns(rows, setVisibleKeys);

  const openCompanyDetail = useCallback((stagedId) => {
    const company = companyById.get(stagedId);
    if (!company) return;
    if (!isCompanyActionEligible(company)) {
      setCompanyDetail(company);
      setCompanyDetailOpen(true);
      return;
    }
    Promise.resolve(registerCompanyInquiry(company.stagedId)).finally(() => {
      setCompanyDetail(company);
      setCompanyDetailOpen(true);
    });
  }, [companyById]);

  const handleCellClick = useCallback(
    ({ column, row }, event) => {
      if (!row?.id || isSelectColumnKey(column?.key)) return;
      if (event?.target?.closest?.(ROW_INTERACTIVE_SELECTOR)) return;
      openCompanyDetail(row.id);
    },
    [openCompanyDetail]
  );

  const sortedRows = useMemo(
    () => sortGridRows(rows, sortColumns, statusMap),
    [rows, sortColumns, statusMap]
  );

  const latestEnrichmentSummary = useMemo(() =>
    getLatestEnrichmentSummary(enrichmentColumns, latestEnrichmentAction, workspaceEnrichmentSummaryStatusMap?.[latestEnrichmentAction] ? workspaceEnrichmentSummaryStatusMap : statusMap),
    [enrichmentColumns, latestEnrichmentAction, statusMap, workspaceEnrichmentSummaryStatusMap]
  );

  const recomputeColumnWidths = useCallback(() => {
    const el = gridWrapRef.current;
    if (!el) return;
    setContainerWidth(el.getBoundingClientRect().width);
  }, []);

  useEffect(() => {
    recomputeColumnWidths();
  }, [recomputeColumnWidths]);

  useEffect(() => {
    const el = gridWrapRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(() => recomputeColumnWidths());
    ro.observe(el);
    return () => ro.disconnect();
  }, [recomputeColumnWidths]);

  const getBodyRowHeight = useCallback(
    (row) => calculateBodyRowHeight(
      row,
      BASE_ROW_HEIGHT,
      ENRICHED_ROW_HEIGHT,
      enrichmentColumns,
      statusMap
    ),
    [BASE_ROW_HEIGHT, ENRICHED_ROW_HEIGHT, enrichmentColumns, statusMap]
  );

  // Exact pixel height for the current page of rows — no fixed viewport / inner scroll.
  const contentGridHeight = useMemo(
    () => getContentGridHeight(
      sortedRows,
      HEADER_HEIGHT,
      BASE_ROW_HEIGHT,
      getBodyRowHeight
    ),
    [BASE_ROW_HEIGHT, HEADER_HEIGHT, getBodyRowHeight, sortedRows]
  );

  const activeColumnKeys = useMemo(
    () => getActiveColumnKeys(visibleKeys, DEFAULT_VISIBLE),
    [visibleKeys]
  );

  useEffect(() => {
    setSortColumns((current) =>
      reconcileSortColumns(current, activeColumnKeys, enrichmentColumns)
    );
  }, [activeColumnKeys, enrichmentColumns]);

  const openEmailsViewer = useCallback((rowId, companyName, emails) => {
    setEmailsDialog({ open: true, rowId, companyName: companyName || 'Company', emails: normalizeEmails(emails) });
  }, []);

  const closeEmailsViewer = useCallback(() => {
    setEmailsDialog({ open: false, rowId: null, companyName: '', emails: [] });
  }, []);

  const openLocationsViewer = useCallback((rowId, companyName, locations) => {
    setLocationsDialog({
      open: true,
      rowId,
      companyName: companyName || 'Company',
      locations: Array.isArray(locations) ? locations.filter(Boolean) : [],
    });
  }, []);

  const closeLocationsViewer = useCallback(() => {
    setLocationsDialog({ open: false, rowId: null, companyName: '', locations: [] });
  }, []);

  const updateEmailAndComputeBest = useCallback((rowId, email, update) => {
    setEmailVerification((prev) => {
      const result = applyEmailVerificationUpdate(prev, rowId, email, update);
      setStatusMap((current) =>
        setStatusEntry(current, 'verify_email', rowId, result.rowStatus)
      );
      return result.emailVerification;
    });
  }, []);

  const newCompanyIdSet = useMemo(() => toNewCompanyIdSet(newCompanyIds), [newCompanyIds]);

  const makeColumnDef = useCallback(
    (colKey) => {
      // Sortable company columns: Name, Industry, Size (Employees), Location (State/Location).
      if (colKey === 'description' || colKey === 'industry') {
        return {
          key: colKey,
          name: colKey.charAt(0).toUpperCase() + colKey.slice(1),
          sortable: colKey === 'industry',
          renderCell: ({ row }) => (
            <Tooltip title={row[colKey]} placement="top-start" arrow>
              <Box sx={{ px: 1, height: '100%', display: 'flex', alignItems: 'center', minWidth: 0 }}>
                <Typography variant="body2" sx={{ fontSize: '13px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {row[colKey]}
                </Typography>
              </Box>
            </Tooltip>
          )
        };
      }

      if (colKey === 'location') {
        return {
          key: 'location',
          name: 'Location',
          sortable: true,
          renderCell: ({ row }) => {
            const locations = Array.isArray(row.locations) && row.locations.length > 0
              ? row.locations
              : (row.location && row.location !== 'N/A' ? [row.location] : []);
            if (locations.length === 0) {
              return (
                <Box sx={{ px: 1, height: '100%', display: 'flex', alignItems: 'center' }}>
                  N/A
                </Box>
              );
            }

            const first = locations[0];
            const remaining = locations.length - 1;

            return (
              <Tooltip
                title={remaining > 0 ? 'Click to view all locations' : first}
                arrow
                placement="top"
              >
                <Box
                  onClick={
                    remaining > 0
                      ? (e) => {
                          e.stopPropagation();
                          openLocationsViewer(row.id, row.name, locations);
                        }
                      : undefined
                  }
                  onMouseDown={remaining > 0 ? (e) => e.stopPropagation() : undefined}
                  sx={{
                    px: 1,
                    height: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    minWidth: 0,
                    cursor: remaining > 0 ? 'pointer' : 'default',
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{
                      fontSize: '13px',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      minWidth: 0,
                      flex: 1,
                    }}
                  >
                    {first}
                  </Typography>
                  {remaining > 0 ? (
                    <Chip size="small" label={`+${remaining}`} sx={MULTI_VALUE_COUNT_CHIP_SX} />
                  ) : null}
                </Box>
              </Tooltip>
            );
          },
        };
      }

      if (colKey === 'size') {
        return {
          key: 'size',
          name: 'Size',
          sortable: true,
          renderCell: ({ row }) => (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                height: '100%',
                width: '100%',
                minWidth: 0,
                px: 1,
                overflow: 'hidden',
                boxSizing: 'border-box',
                '& .MuiChip-root': {
                  maxWidth: '100%',
                },
                '& .MuiChip-label': {
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                },
              }}
            >
              {renderHeadcountBucket(row.size, row.sizeLabel, row.sizeEstimated)}
            </Box>
          )
        };
      }

      if (colKey === 'domain') {
        return {
          key: 'domain',
          name: 'Domain',
          sortable: false,
          renderCell: ({ row }) => (
            <Box sx={{ display: 'flex', alignItems: 'center', height: '100%', width: '100%', px: 1, gap: 0.5, minWidth: 0 }}>
              <IconButton
                size="small"
                onClick={(event) => {
                  event.stopPropagation();
                  const dom = row.domain;
                  if (!dom || dom === 'N/A') return;
                  const url = dom.startsWith('http') ? dom : `https://${dom}`;
                  window.open(url, '_blank', 'noopener,noreferrer');
                }}
                onMouseDown={(event) => event.stopPropagation()}
                sx={{ padding: '2px', color: theme.palette.text.secondary, '&:hover': { color: theme.palette.primary.main } }}
              >
                <OpenInNewIcon sx={{ fontSize: '16px' }} />
              </IconButton>
              <Typography variant="body2" sx={{ fontSize: '13px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {row.domain}
              </Typography>
            </Box>
          )
        };
      }

      if (colKey === 'emails') {
        return {
          key: 'emails',
          name: 'Emails',
          sortable: false,
          renderCell: ({ row }) => {
            const found = row.foundEmail;
            const clean = normalizeEmails(row.emails);
            if (clean.length === 0) return <Box sx={{ px: 1, height: '100%', display: 'flex', alignItems: 'center' }}>N/A</Box>;

            const first = clean[0];
            const remaining = clean.length - 1;

            return (
              <Tooltip title="Click to view/copy all emails" arrow placement="top">
                <Box
                  onClick={(e) => { e.stopPropagation(); openEmailsViewer(row.id, row.name, clean); }}
                  onMouseDown={(e) => e.stopPropagation()}
                  sx={{ px: 1, height: '100%', display: 'flex', alignItems: 'center', gap: 1, minWidth: 0, cursor: 'pointer' }}
                >
                  <Box sx={{ minWidth: 0, flex: 1, lineHeight: 1.2 }}>
                    <Typography variant="body2" sx={{ fontSize: '13px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {first}
                    </Typography>
                    {found && (
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontSize: '11px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {foundEmailLabel(found)}
                      </Typography>
                    )}
                  </Box>
                  {remaining > 0 && <Chip size="small" label={`+${remaining}`} sx={MULTI_VALUE_COUNT_CHIP_SX} />}
                </Box>
              </Tooltip>
            );
          }
        };
      }

      if (colKey === 'phone' || colKey === 'revenue') {
        return {
          key: colKey,
          name: colKey.charAt(0).toUpperCase() + colKey.slice(1),
          sortable: false,
          renderCell: ({ row }) => (
            <Box sx={{ px: 1, height: '100%', display: 'flex', alignItems: 'center', minWidth: 0 }}>
              <Typography variant="body2" sx={{ fontSize: '13px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {colKey === 'phone' ? fmtPhone(row.phone) : (row.revenueLabel ?? NOT_REPORTED)}
              </Typography>
            </Box>
          )
        };
      }

      if (colKey === 'name') {
        return {
          key: 'name',
          name: 'Name',
          sortable: true,
          renderCell: ({ row }) => (
            <Box
              sx={{
                px: 1,
                height: '100%',
                width: '100%',
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                minWidth: 0,
                overflow: 'hidden',
                boxSizing: 'border-box',
              }}
            >
              <Avatar
                src={row.logo || defaultImageUrl}
                alt=""
                variant="rounded"
                imgProps={{
                  onError: (event) => {
                    event.currentTarget.onerror = null;
                    event.currentTarget.src = defaultImageUrl;
                  },
                }}
                sx={{
                  width: 22,
                  height: 22,
                  flexShrink: 0,
                  bgcolor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.08)' : '#F3F3F4',
                  border: `1px solid ${
                    theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.12)' : 'rgba(88,89,91,0.14)'
                  }`,
                  '& img': { objectFit: 'contain' },
                }}
              >
                <BuildingIcon sx={{ fontSize: 14 }} />
              </Avatar>
              <Tooltip title={row.name} placement="top-start" arrow>
                <Typography
                  variant="body2"
                  sx={{
                    fontSize: '13px',
                    fontWeight: 550,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    minWidth: 0,
                    flex: 1,
                  }}
                >
                  {row.name}
                </Typography>
              </Tooltip>
              {newCompanyIdSet?.has(String(row.id)) ? <EnrichmentGridNewCompanyChip /> : null}
              {row.readOnly && <Typography variant="caption" sx={{ flexShrink: 0 }}>Read-only</Typography>}
            </Box>
          ),
        };
      }

      return { key: 'name', name: 'Name', sortable: true };
    },
    [newCompanyIdSet, openEmailsViewer, openLocationsViewer, theme]
  );

  const makeEnrichmentColumn = useCallback((actionKey, displayName) => {
    const isLongForm = isLongFormEnrichmentAction(actionKey);
    const entries = Object.values(statusMap[actionKey] || {}).filter(Boolean);
    const columnStatus = deriveGeneratedColumnStatus(entries);
    const isProcessing = entries.length > 0 && isGeneratedColumnHighlightActive(columnStatus);

    return {
      key: `enrich_${actionKey}`,
      name: displayName,
      sortable: false,
      cellClass: (row) => {
        const classes = [GENERATED_COLUMN_CELL_CLASS];
        if (isProcessing) {
          classes.push(GENERATED_COLUMN_PROCESSING_CLASS);
        }
        if (isGeneratedCellResultSettled((statusMap[actionKey] || {})[row.id])) {
          classes.push(GENERATED_COLUMN_RESULT_CLASS);
        }
        return classes.join(" ");
      },
      headerCellClass: isProcessing ? GENERATED_COLUMN_HEADER_PROCESSING_CLASS : undefined,
      renderHeaderCell: ({ column, sortDirection }) => (
        <GeneratedColumnHeaderCell
          name={column.name}
          sortDirection={sortDirection}
          isProcessing={isProcessing}
          sortable={Boolean(column.sortable)}
        />
      ),
      renderCell: ({ row }) => (
        <GeneratedColumnResultCell
          actionKey={actionKey}
          entry={(statusMap[actionKey] || {})[row.id]}
          leadContacts={row.leadContacts}
          isLongForm={isLongForm}
          formatVerifyEmailCellError={formatVerifyEmailCellError}
          theme={theme}
        />
      ),
    };
  }, [statusMap, theme]);

  const computedColumns = useMemo(
    () => buildGridColumns({
      activeColumnKeys,
      containerWidth,
      enrichmentColumns,
      isMobile,
      makeBaseColumn: makeColumnDef,
      makeEnrichmentColumn,
      renderHeaderCell: renderSpreadsheetHeaderCell,
      selectColumn: selectionColumn,
      workspaceEnrichmentColumnLabels: {...aiRunLabels, ...workspaceEnrichmentColumnLabels},
    }),
    [activeColumnKeys, aiRunLabels, containerWidth, enrichmentColumns, isMobile, makeColumnDef, makeEnrichmentColumn, selectionColumn, workspaceEnrichmentColumnLabels]
  );

  // Scroll a generated column into view only when something explicitly asks for it.
  // Generating a column deliberately does NOT auto-scroll (owner ruling 2026-08-23):
  // yanking the grid right hid the frozen base columns mid-task.
  useEffect(() => {
    const actionKey = focusGeneratedColumnKey;
    if (!actionKey) return;
    const columnKey = `enrich_${actionKey}`;
    const columnIdx = computedColumns.findIndex((column) => column.key === columnKey);
    if (columnIdx < 0) return;

    const frame = requestAnimationFrame(() => {
      // Only pull the column into view when it is genuinely off-screen. Scrolling a
      // column that already fits yanks the frozen base columns out of view mid-task.
      const gridEl = gridWrapRef.current?.querySelector('.rdg');
      const headerCell = gridEl?.querySelector(
        `[role="columnheader"][aria-colindex="${columnIdx + 1}"]`
      );
      let alreadyVisible = false;
      if (gridEl && headerCell) {
        const gridRect = gridEl.getBoundingClientRect();
        const cellRect = headerCell.getBoundingClientRect();
        alreadyVisible = cellRect.left >= gridRect.left && cellRect.right <= gridRect.right;
      }
      if (!alreadyVisible) {
        dataGridRef.current?.scrollToCell?.({ idx: columnIdx, rowIdx: 0 });
      }
      onFocusGeneratedColumnConsumed?.(actionKey);
    });
    return () => cancelAnimationFrame(frame);
  }, [computedColumns, enrichmentColumns, focusGeneratedColumnKey, onFocusGeneratedColumnConsumed]);

  const handleSortColumnsChange = useCallback((nextSortColumns) => {
    const activeSort = nextSortColumns[nextSortColumns.length - 1];
    setSortColumns(activeSort ? [activeSort] : []);
  }, []);

  const handleColumnsMenuOpen = (e) => setColumnsMenuAnchor(e.currentTarget);
  const handleColumnsMenuClose = () => setColumnsMenuAnchor(null);

  const toggleColumn = (key) => {
    markColumnDecided(key);
    setVisibleKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const resetColumns = () => {
    setVisibleKeys(new Set(DEFAULT_VISIBLE));
    try { localStorage.setItem(STORAGE_KEY_VISIBLE_COLS, JSON.stringify(Array.from(DEFAULT_VISIBLE))); } catch {}
  };

  const safeSetStatus = (actionKey, rowId, update) => {
    setStatusMap((current) => setStatusEntry(current, actionKey, rowId, update));
  };

  const applyLeadDerivedColumns = useCallback((rowId, derivedColumns, afterActionKey) => {
    const derivedEntries = normalizeLeadDerivedColumns(derivedColumns);
    if (!rowId || derivedEntries.length === 0) return 0;

    setEnrichmentColumns((prev) => {
      return insertLeadContactColumns(prev, derivedEntries, afterActionKey);
    });
    setStatusMap((prev) => mergeLeadDerivedStatusMap(prev, rowId, derivedEntries));

    return derivedEntries.length;
  }, []);

  const removeGeneratedEnrichmentAction = useCallback((actionKey) => {
    if (!actionKey?.startsWith('ai_enrichment')) return;
    setEnrichmentColumns((prev) => prev.filter((column) => column !== actionKey));
    setStatusMap((prev) => {
      if (!prev?.[actionKey]) return prev;
      const next = { ...(prev || {}) };
      delete next[actionKey];
      return next;
    });
    setLatestEnrichmentAction((current) => (current === actionKey ? null : current));
  }, []);

  const verifySingleEmail = (rowId, email) => runEmailVerification(rowId, [email], async () => {
    if (!isCompanyActionEligible(companyById.get(rowId))) return;
    const current = (emailVerification[rowId] || {})[email];
    if (current?.status === 'running') return;

    updateEmailAndComputeBest(rowId, email, { status: 'running', error: null });

    try {
      const resp = await verifyEmailBulkApi({ emails: [email] });
      const data = resp?.data?.results?.[0]; 

      if (data?.status === 'success') {
        updateEmailAndComputeBest(rowId, email, { status: 'success', score: data.score, lastVerifiedAt: new Date().toISOString() });
      } else {
        updateEmailAndComputeBest(rowId, email, { status: 'failed', error: data?.error || 'Failed' });
      }
    } catch (error) {
      const { status, msg } = parseApiError(error, 'Verification failed');
      const cellError = formatVerifyEmailCellError(msg);
      if (status === 402) setSnackbar({ open: true, message: 'Out of credits.', severity: 'warning' });
      else setSnackbar({ open: true, message: cellError, severity: 'error' });
      
      updateEmailAndComputeBest(rowId, email, { status: 'failed', error: cellError });
    }
  });

  const verifyAllEmails = (rowId, emailsForRow = []) => runEmailVerification(rowId, normalizeEmails(emailsForRow), async () => {
    if (!isCompanyActionEligible(companyById.get(rowId))) return;
    const cleaned = normalizeEmails(emailsForRow);

    if (cleaned.length === 0) {
      setSnackbar({ open: true, message: 'No emails to verify', severity: 'info' });
      return;
    }

    cleaned.forEach((email) => updateEmailAndComputeBest(rowId, email, { status: 'running', error: null }));

    try {
      const resp = await verifyEmailBulkApi({ emails: cleaned, provider: 'hunter' });
      const results = resp?.data?.results || [];

      if (!results.length) throw new Error('No response from verification endpoint');

      let hasSuccess = false;
      let highestScore = 0;

      results.forEach((result) => {
        if (result.status === 'success') {
          hasSuccess = true;
          highestScore = Math.max(highestScore, result.score || 0);
          updateEmailAndComputeBest(rowId, result.email, { status: 'success', score: result.score, lastVerifiedAt: new Date().toISOString() });
        } else {
          updateEmailAndComputeBest(rowId, result.email, { status: 'failed', error: result.error || 'Failed' });
        }
      });

      if (hasSuccess) safeSetStatus('verify_email', rowId, { status: 'success', result: highestScore });
      else safeSetStatus('verify_email', rowId, { status: 'failed', error: 'No successful verifications' });
      
      setSnackbar({ open: true, message: `Verified ${cleaned.length} emails`, severity: 'success' });
    } catch (error) {
      const { status, msg } = parseApiError(error, 'Verification failed');
      const cellError = formatVerifyEmailCellError(msg);
      if (status === 402) setSnackbar({ open: true, message: 'Out of credits.', severity: 'warning' });
      else setSnackbar({ open: true, message: cellError, severity: 'error' });
      
      cleaned.forEach((email) => updateEmailAndComputeBest(rowId, email, { status: 'failed', error: cellError }));
    }
  });

  const runEnrichmentAction = async (actionType, options = {}) => {
    const selectedIds = (Array.isArray(options.companyIds)
      ? options.companyIds
      : Array.from(selectedRows)).filter((id) => id && !unavailableIds.has(id));
    if (selectedIds.length === 0) {
      setSnackbar({ open: true, message: 'Select at least one company', severity: 'warning' });
      return;
    }
    if (actionType === 'ai_enrichment' && !String(options.query || '').trim()) {
      setSnackbar({ open: true, message: 'Please enter an AI prompt', severity: 'warning' });
      return;
    }
    let actionKey = actionType;
    if (actionType === 'ai_enrichment') {
      const aiRunCount = enrichmentColumns.filter(c => c.startsWith('ai_enrichment')).length + 1;
      actionKey = `ai_enrichment_${aiRunCount}`;
    }
    // Close the action panel, append the column immediately, and focus it.
    closeActions();
    setLatestEnrichmentAction(actionKey);
    setEnrichmentColumns((prev) => prev.includes(actionKey) ? prev : [...prev, actionKey]);
    const loadingGate = createGeneratedColumnLoadingGate();
    const reportStatus = (key, id, entry) => {
      safeSetStatus(key, id, entry);
      if (typeof options.onCompanyStatus !== 'function') return;
      if (!selectedIds.includes(id)) return;
      const baseType = String(key).startsWith('ai_enrichment') ? 'ai_enrichment' : key;
      const payload = { actionType: baseType, ...entry };
      if (baseType === 'find_email' && entry.status === 'success') {
        payload.title = 'Email found';
        payload.email = entry.result;
      }
      if (baseType === 'verify_email' && entry.status === 'success') {
        const row = rowById.get(id);
        const emails = normalizeEmails(row?.emails);
        const email = emails[0];
        payload.title = 'Email verified';
        payload.email = email;
        payload.detail = email
          ? `${email} · ${entry.result >= 70 ? 'Deliverable' : `Score ${entry.result}%`}`
          : `Score ${entry.result}%`;
      }
      if (baseType === 'ai_enrichment' && entry.status === 'success') {
        payload.title = 'AI insight';
        payload.summary = entry.summary ?? entry.result;
      }
      options.onCompanyStatus(payload);
    };
    if (actionType === 'find_and_verify_email') {
      await runCombinedEmailForCompanies({ run: combinedEmailRunner.current, companyIds: selectedIds,
        draftSessionId, workspaceId, onDraftSessionUsed, isEligible: (id) => isCompanyActionEligible(companyById.get(id)),
        waitBeforeReveal: () => loadingGate.waitBeforeReveal(),
        onStatus: (id, entry) => reportStatus(actionKey, id, entry),
        onTerminal: (data) => setLiveFoundEmails((prev) => {
          const next = { ...prev };
          Object.entries(foundEmailsFromTerminal(data)).forEach(([id, found]) => {
            const snapshot = (companies || []).find((company) => company?.stagedId === id)?.foundEmail ?? null;
            next[id] = { found, snapshot };
          });
          return next;
        }),
        onVerified: (id, entry) => updateEmailAndComputeBest(id, entry.email,
          { status: 'success', score: entry.score, lastVerifiedAt: new Date().toISOString() }) });
    } else if (actionType === 'find_email') {
      let eligibleCount = 0;
      const runningIds = [];
      const validCompanyIds = [];
      const uniqueDomains = new Set(); 
      const domainToRowIds = new Map();
      selectedIds.forEach((id) => {
        const row = rowById.get(id);
        const domain = row ? extractDomain(row.domain) : null;
        if (!row) reportStatus(actionKey, id, { status: 'missing', error: 'Row not found' });
        else if (!draftSessionId && !domain) {
          reportStatus(actionKey, id, { status: 'failed', error: 'Not found' });
        }
        else {
          eligibleCount += 1;
          runningIds.push(id);
          validCompanyIds.push(id);
          if (domain) {
            uniqueDomains.add(domain);
            if (!domainToRowIds.has(domain)) domainToRowIds.set(domain, []);
            domainToRowIds.get(domain).push(id);
          }
          reportStatus(actionKey, id, { status: 'running' });
        }
      });
      const payloadDomains = Array.from(uniqueDomains);
      if (!draftSessionId && !payloadDomains.length) {
        await loadingGate.waitBeforeReveal();
        runningIds.forEach((id) => reportStatus(actionKey, id, { status: 'failed', error: 'Not found' }));
        return;
      }
      if (!validCompanyIds.length) return;

      setSnackbar({ open: true, message: `Queued ${eligibleCount} companies`, severity: 'info' });

      try {
        const resp = draftSessionId
          ? await findSpreadsheetEmailBulkApi({ company_ids: validCompanyIds, draft_session_id: draftSessionId })
          : await findEmailBulkApi({ domain: payloadDomains });
        if (draftSessionId) onDraftSessionUsed();
        const results = resp?.data?.results || [];
        const completedRowIds = new Set();

        await loadingGate.waitBeforeReveal();
        
        results.forEach((r) => {
          const targetRowIds = r.company_id ? [r.company_id] : (domainToRowIds.get(r.domain) || []);
          
          targetRowIds.forEach((rowId) => {
            completedRowIds.add(rowId);
            if (r.found) reportStatus(actionKey, rowId, { status: 'success', result: r.email });
            else if (r.waterfall_status === 'exhausted' || r.waterfall_status === 'partial_skip') {
              reportStatus(actionKey, rowId, { status: 'failed', error: 'Not found' });
            } else {
              reportStatus(actionKey, rowId, { status: 'failed', error: 'Failed' });
            }
          });
        });
        runningIds.forEach((rowId) => {
          if (!completedRowIds.has(rowId)) reportStatus(actionKey, rowId, { status: 'failed', error: 'Failed' });
        });
      } catch (error) {
        const { msg } = parseApiError(error, 'Find Email failed');
        await loadingGate.waitBeforeReveal();
        runningIds.forEach((id) => reportStatus(actionKey, id, { status: 'failed', error: msg }));
      }
    } else if (actionType === 'verify_email') {
      let eligibleCount = 0;
      const runningIds = [];
      const uniqueEmails = new Set();
      const emailToRowIds = new Map();
      const verifyItems = [];

      selectedIds.forEach((id) => {
        const row = rowById.get(id);
        const emails = row ? verifiableEmails(row, normalizeEmails) : [];
        if (!row) reportStatus(actionKey, id, { status: 'missing', error: 'Row not found' });
        else if (!emails.length) reportStatus(actionKey, id, { status: 'missing', error: 'Missing emails' });
        else {
          eligibleCount += 1;
          runningIds.push(id);
          reportStatus(actionKey, id, { status: 'running' });
          emails.forEach(e => {
            uniqueEmails.add(e);
            verifyItems.push({ company_id: id, email: e });
            if (!emailToRowIds.has(e)) emailToRowIds.set(e, []);
            emailToRowIds.get(e).push(id);
          });
        }
      });

      const payloadEmails = Array.from(uniqueEmails);
      if (!payloadEmails.length) return;

      setSnackbar({ open: true, message: `Queued ${eligibleCount} for Verification`, severity: 'info' });

      try {
        const resp = draftSessionId
          ? await verifySpreadsheetEmailBulkApi({ items: verifyItems, provider: 'hunter', draft_session_id: draftSessionId })
          : await verifyEmailBulkApi({ emails: payloadEmails, provider: 'hunter' });
        if (draftSessionId) onDraftSessionUsed();
        const results = resp?.data?.results || [];
        
        const bestScoreByCompany = new Map();
        const hasSuccessByCompany = new Set();

        results.forEach((r) => {
          const targetRowIds = r.company_id ? [r.company_id] : (emailToRowIds.get(r.email) || []);
          
          targetRowIds.forEach((rowId) => {
            if (r.status === 'success') {
              hasSuccessByCompany.add(rowId);
              const currentBest = bestScoreByCompany.get(rowId) || 0;
              bestScoreByCompany.set(rowId, Math.max(currentBest, r.score || 0));
              updateEmailAndComputeBest(rowId, r.email, { status: 'success', score: r.score, lastVerifiedAt: new Date().toISOString() });
            } else {
              updateEmailAndComputeBest(rowId, r.email, { status: 'failed', error: r.error || 'Failed' });
            }
          });
        });

        await loadingGate.waitBeforeReveal();
        runningIds.forEach(id => {
          if (hasSuccessByCompany.has(id)) {
             reportStatus(actionKey, id, { status: 'success', result: bestScoreByCompany.get(id) });
          } else {
             reportStatus(actionKey, id, { status: 'failed', error: 'No verifications succeeded' });
          }
        });

      } catch (error) {
        const { msg } = parseApiError(error, 'Verify Email failed');
        const cellError = formatVerifyEmailCellError(msg);
        await loadingGate.waitBeforeReveal();
        runningIds.forEach((id) => reportStatus(actionKey, id, { status: 'failed', error: cellError }));
      }
    } else if (actionType === 'ai_enrichment') {
      const query = String(options.query || '').trim();
      const modelIdStr = String(options.model || DEFAULT_SPREADSHEET_MODEL_ID).trim();
      
      const modelObj = getSpreadsheetModelById(modelIdStr);
      const llmProvider = modelObj.provider;

      // Grid runs use the durable batch worker (90s per item); Company Details keeps /execute unless it attaches to a saved search.
      if (selectedIds.length === 1 && !options.workspaceQuote && typeof options.onCompanyStatus === 'function') {
        const id = selectedIds[0];
        reportStatus(actionKey, id, { status: 'running' });
        let outcome = null;
        try {
          const resp = await executeAiEnrichmentApi({
            company_id: id,
            query,
            llm_provider: llmProvider,
            model: modelIdStr,
            ...(draftSessionId ? { draft_session_id: draftSessionId } : {})
          });
          const sum = String(resp?.data?.summary || '').trim();
          const payload = resp?.data || {};
          outcome = { type: 'success', sum, payload };
        } catch (error) {
          const { status, msg } = parseApiError(error, 'AI enrichment failed');
          outcome = { type: 'error', status, msg };
        }

        await loadingGate.waitBeforeReveal();

        if (outcome?.type === 'success') {
          const { sum, payload } = outcome;
          const derivedCount = applyLeadDerivedColumns(id, payload.derived_columns, actionKey), leadRun = isLeadRun(payload);
          if (leadRun) setAiRunLabels((prev) => ({...prev, [actionKey]: query}));
          const result = leadRun ? {summary: sum, resultId: payload.result_id, ...classifyLeadRow({leadOutcome: payload.lead_outcome,
            derivedColumns: payload.derived_columns,
            chargedUnits: payload.charged_units})} : classifySingleAiEnrichment(payload);
          reportStatus(actionKey, id, { ...result, ...(result.detail ? { error: result.detail } : {}) });
          if (draftSessionId && (sum || derivedCount > 0 || leadRun)) onDraftSessionUsed();
        } else if (outcome?.type === 'error') {
          const { status, msg } = outcome;
          if (msg.toLowerCase().includes('llm provider not found')) {
            reportStatus(actionKey, id, { status: 'failed', error: 'Provider configuration error' });
            setSnackbar({ open: true, message: msg, severity: 'error' });
          } else if (status === 402) {
            reportStatus(actionKey, id, { status: 'failed', error: 'Out of credits' });
            setSnackbar({ open: true, message: 'Out of credits.', severity: 'warning' });
          } else {
            reportStatus(actionKey, id, { status: 'failed', error: msg });
          }
        }
      } else {
        selectedIds.forEach(id => reportStatus(actionKey, id, { status: 'running' }));
        const saved = Boolean(options.workspaceQuote);
        try {
          const submitResp = await submitAiEnrichmentBatch({
            companyIds: selectedIds, query, provider: llmProvider, model: modelIdStr,
            draftSessionId, workspaceQuote: options.workspaceQuote,
          });
          const batchId = submitResp?.data?.batch_id;
          if (!batchId) throw new Error("No batch ID returned");
          setSnackbar(batchSubmittedNotice(selectedIds.length, saved));
          if (draftSessionId && !saved) onDraftSessionUsed();
          batchWatchers.current.add(watchAiBatch({
            batchId, companyIds: selectedIds, saved, setSnackbar,
            waitBeforeReveal: () => loadingGate.waitBeforeReveal(),
            report: (id, entry) => reportStatus(actionKey, id, entry),
            applyLeadColumns: (id, derivedColumns) => applyLeadDerivedColumns(id, derivedColumns, actionKey),
            markLeadRun: () => setAiRunLabels((prev) => ({...prev, [actionKey]: query})),
            settleRunning: (entry) => setStatusMap((prev) => settleRunningEntries(prev, actionKey, selectedIds, entry)),
          }));
        } catch (error) {
          // #521: no batch exists when submission fails, so the optimistic column goes with it.
          const { status, msg } = parseApiError(error, 'Batch submission failed');
          await loadingGate.waitBeforeReveal();
          removeGeneratedEnrichmentAction(actionKey);
          const notice = batchSubmissionErrorNotice(status, msg, saved, error?.response?.data);
          setSnackbar(notice);
          options.onCompanyStatus?.({ actionType: 'ai_enrichment', status: 'failed', error: notice.message });
          return {requote: Boolean(notice.requote)};
        }
      }
    }
  };
  const searchAi = useSearchAiEnrichmentReview({selectedRows, unavailableIds, query: aiPrompt,
    model: aiModelId, searchContext: searchEnrichmentContext, closeActions, setSnackbar,
    runPaidAction, runEnrichmentAction, countDataUnits: (actionId, ids) => (actionId !== 'verify_email' ? ids.length
      : ids.reduce((total, id) => total + verifiableEmails(rowById.get(id), normalizeEmails).length, 0))});
  const hasSelection = selectedRows.size > 0;
  const inverseSelectionToolbar = hasSelection;
  // Match dark-mode paper surface for the bulk-selection invert state.
  const inverseBg = "#1E1E1E";
  const inverseFg = "#FFFFFF";
  return (
    <MainContainer>
      <GridSection>
        <GridTopBar>
          <Box
            data-testid="spreadsheet-toolbar"
            data-selection-active={inverseSelectionToolbar ? "true" : "false"}
            sx={{
              p: 1.5,
              display: 'flex', flexWrap: 'wrap', rowGap: 1,
              justifyContent: 'space-between',
              borderBottom: 1,
              borderColor: inverseSelectionToolbar ? 'rgba(255,255,255,0.12)' : 'divider',
              bgcolor: inverseSelectionToolbar ? inverseBg : 'background.paper',
              color: inverseSelectionToolbar ? inverseFg : 'text.primary',
              transition:
                'background-color 220ms ease, color 220ms ease, border-color 220ms ease',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1, minWidth: 0 }}>
              <Badge
                badgeContent={rows.length}
                color="primary"
                sx={{
                  '& .MuiBadge-badge': {
                    borderRadius: '4px',
                  },
                }}
              >
                <BuildingIcon
                  fontSize="small"
                  sx={{ color: inverseSelectionToolbar ? inverseFg : 'inherit' }}
                />
              </Badge>
              <Typography
                variant="subtitle2"
                sx={{
                  fontWeight: 700,
                  ml: 1,
                  color: inverseSelectionToolbar ? inverseFg : 'text.primary',
                  transition: 'color 220ms ease',
                }}
              >
                {selectedRows.size} selected
              </Typography>
              {latestEnrichmentSummary && (
                <Chip
                  color={inverseSelectionToolbar ? latestEnrichmentSummary.color : undefined}
                  icon={latestEnrichmentSummary.running ? <CircularProgress size={12} thickness={5} color="inherit" /> : undefined}
                  label={latestEnrichmentSummary.label}
                  size="small"
                  variant={
                    inverseSelectionToolbar
                      ? (latestEnrichmentSummary.running ? 'outlined' : 'filled')
                      : 'outlined'
                  }
                  data-testid="enrichment-progress-chip"
                  sx={(theme) => {
                    const isDark = theme.palette.mode === 'dark';
                    return {
                      fontSize: '12px',
                      fontWeight: 700,
                      height: 24,
                      maxWidth: 280,
                      ...(inverseSelectionToolbar
                        ? {
                            bgcolor: 'rgba(255,255,255,0.12)',
                            color: inverseFg,
                            borderColor: 'rgba(255,255,255,0.28)',
                            '& .MuiChip-icon': { color: inverseFg },
                          }
                        : {
                            // Unselected toolbar is white — use Size-column gray, not orange.
                            backgroundColor: isDark ? 'rgba(255, 255, 255, 0.04)' : '#F7F7F8',
                            color: theme.palette.text.primary,
                            borderColor: isDark
                              ? 'rgba(255, 255, 255, 0.18)'
                              : 'rgba(0, 0, 0, 0.12)',
                          }),
                      '& .MuiChip-label': {
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      },
                    };
                  }}
                />
              )}
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 'auto', flexWrap: 'wrap', justifyContent: 'flex-end', maxWidth: '100%' }}>
              <Box component="span" sx={{ display: 'inline-flex' }}>
                <Button
                  ref={columnsButtonRef}
                  variant="outlined"
                  size="small"
                  onClick={handleColumnsMenuOpen}
                  startIcon={<ViewColumnIcon />}
                  sx={{
                    borderRadius: '10px',
                    textTransform: 'none',
                    transition:
                      'background-color 220ms ease, color 220ms ease, border-color 220ms ease',
                    ...(inverseSelectionToolbar
                      ? {
                          color: inverseFg,
                          borderColor: 'rgba(255,255,255,0.75)',
                          '&:hover': {
                            borderColor: inverseFg,
                            bgcolor: 'rgba(255,255,255,0.12)',
                          },
                          '& .MuiButton-startIcon': { color: inverseFg },
                        }
                      : {}),
                  }}
                >
                  Columns
                </Button>
              </Box>

              <Menu
                anchorEl={columnsMenuAnchor}
                open={Boolean(columnsMenuAnchor)}
                onClose={handleColumnsMenuClose}
                PaperProps={{ sx: { minWidth: 240, borderRadius: '12px' } }}
              >
                <Box sx={{ px: 2, pt: 1.5, pb: 1 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>Visible Columns</Typography>
                </Box>
                <Divider />
                {ALL_COLUMNS.map((c) => (
                  <MenuItem key={c.key} dense onClick={() => toggleColumn(c.key)} sx={{ py: 0.5 }}>
                    <Checkbox size="small" checked={activeColumnKeys.has(c.key)} tabIndex={-1} disableRipple />
                    <ListItemText primary={c.name} />
                  </MenuItem>
                ))}
                <Divider />
                <Box sx={{ px: 2, py: 1, display: 'flex', justifyContent: 'flex-end' }}>
                  <Button size="small" variant="text" onClick={resetColumns} sx={{ textTransform: 'none' }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>Reset Defaults</Typography>
                  </Button>
                </Box>
              </Menu>

              <PushToCrmMenu workspaceId={workspaceId ?? searchEnrichmentContext?.workspaceId}
                isDirty={crmSourceDirty || searchEnrichmentContext?.isDirty} selectedIds={selectedRows} />
              <Tooltip title={selectedRows.size === 0 ? 'Select at least one company'
                : actionsPending ? 'A paid action is running. Actions return when it finishes.' : ''}>
                <span>
                  <Button
                    ref={actionsButtonRef}
                    variant="contained"
                    size="small"
                    onClick={openActions}
                    disabled={selectedRows.size === 0 || actionsPending}
                    sx={{
                      borderRadius: '10px',
                      textTransform: 'none',
                      transition:
                        'background-color 220ms ease, color 220ms ease, box-shadow 220ms ease',
                    }}
                  >
                    Actions
                  </Button>
                </span>
              </Tooltip>

              <SpreadsheetActionsMenu
                pending={actionsPending}
                open={Boolean(actionsAnchor)}
                anchorEl={actionsAnchor}
                onClose={closeActions}
                view={actionsView}
                onViewChange={setActionsView}
                selectedCount={selectedRows.size}
                dataEnrichmentSelection={dataEnrichmentSelection}
                onDataEnrichmentSelectionChange={setDataEnrichmentSelection}
                aiPrompt={aiPrompt}
                onAiPromptChange={setAiPrompt}
                aiModelId={aiModelId}
                onAiModelIdChange={setAiModelId}
                onContinueEnrichment={searchAi.requestData}
                confirmFollows={searchAi.confirmFollows}
                onGenerateAi={searchAi.request}
              />
            </Box>
          </Box>
        </GridTopBar>

        <GridBody ref={gridWrapRef}>
          <StyledDataGrid
            ref={dataGridRef}
            columns={computedColumns}
            rows={sortedRows}
            rowKeyGetter={(row) => row.id}
            onCellClick={handleCellClick}
            rowClass={(row) => [
              rowHasGeneratedContent(statusMap, enrichmentColumns, row.id) && GENERATED_ROW_HIGHLIGHT_CLASS,
              flashNewResultRows && newCompanyIdSet?.has(String(row.id)) && HOME_NEW_RESULT_FLASH_CLASS,
            ].filter(Boolean).join(' ') || undefined}
            className={isMobile ? 'enrichment-grid-mobile' : undefined}
            style={{
              height: contentGridHeight,
              width: '100%',
            }}
            selectedRows={selectedRows}
            onSelectedRowsChange={changeSelection}
            sortColumns={sortColumns}
            onSortColumnsChange={handleSortColumnsChange}
            headerRowHeight={HEADER_HEIGHT}
            rowHeight={(args) => {
              const rowObj = args?.type === 'ROW' ? args.row : args;
              return getBodyRowHeight(rowObj);
            }}
          />
        </GridBody>
      </GridSection>

      <SearchAiEnrichmentReview review={searchAi.review} onClose={searchAi.close} onConfirm={searchAi.confirm} onQuoteError={searchAi.onQuoteError} />

      {/* Emails Dialog */}
      {emailsDialog.open && (
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            bgcolor: theme.palette.mode === 'dark' ? 'rgba(0,0,0,0.55)' : 'rgba(0,0,0,0.35)',
            zIndex: 200,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            p: 2
          }}
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) closeEmailsViewer();
          }}
        >
          <Paper elevation={8} sx={{ width: 'min(720px, 95vw)', maxHeight: 'min(560px, 85vh)', borderRadius: '14px', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
            <Box sx={{ px: 2, py: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: 1, borderColor: 'divider' }}>
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>Emails</Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>{emailsDialog.companyName} • {emailsDialog.emails.length} found</Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                  Verify Email costs {DATA_ENRICHMENT_COST_LABEL} per email.
                </Typography>
              </Box>

              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Button size="small" variant="outlined" onClick={() => verifyAllEmails(emailsDialog.rowId, emailsDialog.emails)} disabled={!isCompanyActionEligible(companyById.get(emailsDialog.rowId)) || !emailsDialog.emails || emailsDialog.emails.length === 0 || emailsDialog.emails.some((email) => isEmailPending(emailsDialog.rowId, email))} sx={{ textTransform: 'none' }}>
                  Verify all • {emailsDialog.emails.length} {emailsDialog.emails.length === 1 ? 'credit' : 'credits'}
                </Button>
                <Tooltip title="Copy all emails">
                  <IconButton size="small" onClick={async () => { await copyToClipboard(emailsDialog.emails.join('\n')); }}>
                    <ContentCopyIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <IconButton size="small" onClick={closeEmailsViewer}>
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Box>
            </Box>

            <Box sx={{ p: 2, overflow: 'auto', display: 'flex', flexDirection: 'column', gap: 1 }}>
              {emailsDialog.emails.map((email) => {
                const st = (emailVerification[emailsDialog.rowId] || {})[email] || { status: 'idle' };

                const scoreChip = (score) => {
                  if (score == null) return null;
                  const sxColor = score >= 80 ? { bgcolor: 'success.main', color: 'white' } : score >= 60 ? { bgcolor: 'warning.main', color: 'black' } : { bgcolor: 'error.main', color: 'white' };
                  return <Chip size="small" label={`Score: ${score}%`} sx={{ height: '24px', fontWeight: 700, ...sxColor }} />;
                };

                return (
                  <Box key={email} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, border: 1, borderColor: 'divider', borderRadius: '10px', px: 1.25, py: 0.75 }}>
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                      <Typography variant="body2" sx={{ fontSize: '13px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{email}</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {st.status === 'running' && <Typography variant="caption" color="text.secondary">Running...</Typography>}
                      {st.status === 'success' && scoreChip(st.score)}
                      {st.status === 'failed' && (
                        <Typography variant="caption" color="error">
                          {formatVerifyEmailCellError(st.error)}
                        </Typography>
                      )}

                      <Tooltip title="Copy">
                        <IconButton size="small" onClick={async () => { await copyToClipboard(email); }}>
                          <ContentCopyIcon sx={{ fontSize: 18 }} />
                        </IconButton>
                      </Tooltip>

                      {st.status === 'success' ? (
                        <Tooltip title={`Re-verify • ${DATA_ENRICHMENT_COST_LABEL}`}>
                          <IconButton size="small" disabled={!isCompanyActionEligible(companyById.get(emailsDialog.rowId)) || isEmailPending(emailsDialog.rowId, email)} onClick={() => verifySingleEmail(emailsDialog.rowId, email)}>
                            <VerifiedIcon sx={{ fontSize: 18, color: theme.palette.success.main }} />
                          </IconButton>
                        </Tooltip>
                      ) : (
                        <Button size="small" variant="outlined" onClick={() => verifySingleEmail(emailsDialog.rowId, email)} disabled={!isCompanyActionEligible(companyById.get(emailsDialog.rowId)) || st.status === 'running' || isEmailPending(emailsDialog.rowId, email)} sx={{ textTransform: 'none' }}>
                          {st.status === 'failed' ? `Retry • ${DATA_ENRICHMENT_COST_LABEL}` : `Verify • ${DATA_ENRICHMENT_COST_LABEL}`}
                        </Button>
                      )}
                    </Box>
                  </Box>
                );
              })}
            </Box>
          </Paper>
        </Box>
      )}

      {/* Locations Dialog — same overlay pattern as emails */}
      {locationsDialog.open && (
        <Box
          data-testid="locations-dialog"
          sx={{
            position: 'absolute',
            inset: 0,
            bgcolor: theme.palette.mode === 'dark' ? 'rgba(0,0,0,0.55)' : 'rgba(0,0,0,0.35)',
            zIndex: 200,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            p: 2
          }}
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) closeLocationsViewer();
          }}
        >
          <Paper elevation={8} sx={{ width: 'min(720px, 95vw)', maxHeight: 'min(560px, 85vh)', borderRadius: '14px', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
            <Box sx={{ px: 2, py: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: 1, borderColor: 'divider' }}>
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>Locations</Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                  {locationsDialog.companyName} • {locationsDialog.locations.length} registered
                </Typography>
              </Box>

              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Tooltip title="Copy all locations">
                  <IconButton
                    size="small"
                    onClick={async () => {
                      await copyToClipboard(locationsDialog.locations.join('\n'));
                    }}
                  >
                    <ContentCopyIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <IconButton size="small" onClick={closeLocationsViewer} aria-label="Close locations">
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Box>
            </Box>

            <Box sx={{ p: 2, overflow: 'auto', display: 'flex', flexDirection: 'column', gap: 1 }}>
              {locationsDialog.locations.map((location, index) => (
                <Box
                  key={`${location}-${index}`}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 1,
                    border: 1,
                    borderColor: 'divider',
                    borderRadius: '10px',
                    px: 1.25,
                    py: 0.75,
                  }}
                >
                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Typography
                      variant="body2"
                      sx={{
                        fontSize: '13px',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {location}
                    </Typography>
                    {index === 0 ? (
                      <Typography variant="caption" color="text.secondary">
                        Primary
                      </Typography>
                    ) : null}
                  </Box>
                  <Tooltip title="Copy">
                    <IconButton size="small" onClick={async () => { await copyToClipboard(location); }}>
                      <ContentCopyIcon sx={{ fontSize: 18 }} />
                    </IconButton>
                  </Tooltip>
                </Box>
              ))}
            </Box>
          </Paper>
        </Box>
      )}

      <CompanyModal
        open={companyDetailOpen}
        setOpen={setCompanyDetailOpen}
        companyDetail={companyDetail}
        imageData={companyDetail?.primaryLogo || defaultImageUrl}
        draftSessionId={draftSessionId}
        onDraftSessionUsed={onDraftSessionUsed}
        onRunEnrichmentAction={searchAi.runDetailsAction}
      />

      {/* A refused paid run must stay visible until dismissed; routine notices auto-hide. */}
      <Snackbar open={snackbar.open} autoHideDuration={snackbar.persist ? null : 3500}
        onClose={(_, reason) => (snackbar.persist && reason === 'clickaway') || setSnackbar((s) => ({ ...s, open: false }))}>
        <Alert severity={snackbar.severity} onClose={() => setSnackbar((s) => ({ ...s, open: false }))}>{snackbar.message}{snackbar.detail && <Box>{snackbar.detail}</Box>}</Alert>
      </Snackbar>
    </MainContainer>
  );
};

export default EnrichmentGrid;
